import { LayerItem } from './base';
import { GridArea } from './grid';
import { Position, PaddingMode } from '../Utils';
import { MediaObject } from '../media/types';
import { ObjectFit } from './style';
import { 
    validateGridArea, 
    validatePositionGridAreaCombination, 
    detectLegacyUsage 
} from '../validation/GridValidation';
import { getGridAreaForPosition } from '../validation/PositionMapping';

export interface PositionedItem<T extends MediaObject = MediaObject> extends LayerItem {
    id: string;
    startAt: number;
    duration?: number;
    mediaObject: T;
    gridArea?: GridArea;
    scale?: number;
    opacity?: number;
    objectFit?: ObjectFit;
    position?: Position;
    layoutPadding?: number;
    paddingMode?: PaddingMode;
}

export abstract class PositionedItemImpl<T extends MediaObject = MediaObject> implements PositionedItem<T> {
    id: string;
    startAt: number;
    duration?: number;
    mediaObject: T;
    gridArea: GridArea;
    scale: number;
    opacity: number;
    objectFit: ObjectFit;
    position?: Position;
    layoutPadding?: number;
    paddingMode?: PaddingMode;

    constructor(item: Omit<PositionedItem<T>, 'render'>) {
        // 1. Detect and handle legacy usage patterns
        detectLegacyUsage(item);
        
        // 2. Validate position + gridArea combination
        validatePositionGridAreaCombination(item.position, item.gridArea, item.id);
        
        // 3. Handle hybrid positioning system
        let finalGridArea: GridArea;
        let finalPosition: Position | undefined;
        
        if (item.position && !item.gridArea) {
            // Position provided, auto-map to gridArea
            const mappedGridArea = getGridAreaForPosition(item.position);
            if (mappedGridArea) {
                finalGridArea = mappedGridArea;
                // Keep position unset to maintain mutual exclusivity when gridArea is auto-mapped
            } else {
                throw new Error(`Cannot map position '${item.position}' to grid area for item '${item.id}'`);
            }
        } else if (item.gridArea && !item.position) {
            // GridArea provided, use it directly
            finalGridArea = item.gridArea;
            // Keep position unset to maintain mutual exclusivity when gridArea is provided
        } else if (!item.position && !item.gridArea) {
            // Neither provided, use defaults
            finalGridArea = { row: 2, col: 2, rows: 2, cols: 2, gridSize: 5 }; // Center area
            finalPosition = Position.Center;
        } else {
            // Both provided - validatePositionGridAreaCombination already threw error
            throw new Error(`Invalid state: Both position and gridArea provided for item '${item.id}'`);
        }
        
        // 4. Validate final gridArea configuration
        validateGridArea(finalGridArea);
        
        // 5. Set properties
        this.id = item.id;
        this.startAt = item.startAt;
        this.duration = item.duration;
        this.mediaObject = item.mediaObject;
        this.gridArea = finalGridArea;
        this.scale = item.scale ?? 1;
        this.opacity = item.opacity ?? 1;
        this.objectFit = item.objectFit ?? 'scale-down';
        if (finalPosition !== undefined) {
            this.position = finalPosition;
        }
        this.layoutPadding = item.layoutPadding;
        this.paddingMode = item.paddingMode;
    }

    abstract render(fps: number, theme: any, aspectRatio: any, defaults: any): React.ReactNode;
} 